import type { GraphQLFormattedError } from 'graphql';
import { type FetchResult } from '@apollo/client';
import type {
  CopyCommentsModeInput,
  DuplicateAssetsMutation,
} from '@frameio/graphql-client/generated';
import { useDuplicateAssetsMutation } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useClearFocusedAsset } from '@frameio/selection';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { useLoadingToast } from '@frameio/shared/resources';
import {
  hasEsmProjectError,
  isAdobeProject,
} from '@frameio/shared/utils/hasEsmProjectError';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import TEXT from './generated/TEXT/useSyncCopyAssets';

TEXT.DEFINE(
  {
    copyLoading: (count: number): string =>
      plural(count, {
        one: `Copying 1 item…`,
        other: `Copying ${count} items…`,
      }),
    copyErrorMessage: 'Your items could not be copied',
    copySuccessMessage: (count: number): string =>
      plural(count, {
        one: `Copied 1 item`,
        other: `Copied ${count} items`,
      }),
    duplicateLoading: (count: number): string =>
      plural(count, {
        one: `Duplicating 1 item…`,
        other: `Duplicating ${count} items…`,
      }),
    duplicateErrorMessage: 'Your items could not be duplicated',
    duplicateSuccessMessage: (count: number): string =>
      plural(count, {
        one: `Duplicated 1 item`,
        other: `Duplicated ${count} items`,
      }),
    adobeDuplicateSuccessMessage: (count: number): string =>
      plural(count, {
        one: `1 Item(s) duplicated`,
        other: `${count} Item(s) duplicated`,
      }),
    adobeCopySuccessMessage: (count: number): string =>
      plural(count, {
        one: `1 Item(s) copied`,
        other: `${count} Item(s) copied`,
      }),
  },
  { plural }
);

type Props = {
  assetIds: string[];
  destination: AssetParentFragment;
  copyComments: CopyCommentsModeInput;
  duplicate?: boolean;
  forceRefetch?: boolean;
  onError?: () => void;
  onSuccess?: () => void;
};

export const useSyncCopyAssets = () => {
  const [copyAssets, { client }] = useDuplicateAssetsMutation();
  const { addToast, updateAndHideToast } = useLoadingToast();
  const clearFocusedAsset = useClearFocusedAsset();
  const refetchFinder = useFinderRefetch();

  return async ({
    assetIds,
    destination,
    copyComments,
    duplicate,
    onError,
    onSuccess,
    forceRefetch = false,
  }: Props) => {
    const toastId = addToast(
      duplicate
        ? TEXT.duplicateLoading(assetIds.length)
        : TEXT.copyLoading(assetIds.length)
    );

    const projectId = destination.project?.id ?? '';

    const handleError = (
      error: GraphQLFormattedError<Record<string, any>> | undefined
    ) => {
      if (!error) return;
      const esmProjectErrorMessage =
        hasEsmProjectError(
          client,
          projectId,
          error.extensions?.code,
          error.extensions?.value
        ) ?? '';

      let errorMessage = duplicate
        ? TEXT.duplicateErrorMessage
        : TEXT.copyErrorMessage;

      if (!duplicate && esmProjectErrorMessage) {
        errorMessage = esmProjectErrorMessage;
      }

      updateAndHideToast(toastId, {
        title: errorMessage,
        variant: 'error',
      });

      onError?.();
    };

    const handleResolve = (result: FetchResult<DuplicateAssetsMutation>) => {
      if ((result.errors && result.errors.length > 0) || !result.data) {
        handleError(result.errors?.[0]);
        return;
      }

      if (duplicate || forceRefetch) {
        refetchFinder();
      }

      const copiedAssets = result.data?.duplicateAssets?.assets ?? [];

      let successMessage;
      if (isAdobeProject(client, projectId)) {
        successMessage = duplicate
          ? TEXT.adobeDuplicateSuccessMessage(copiedAssets.length)
          : TEXT.adobeCopySuccessMessage(copiedAssets.length);
      } else {
        successMessage = duplicate
          ? TEXT.duplicateSuccessMessage(copiedAssets.length)
          : TEXT.copySuccessMessage(copiedAssets.length);
      }

      updateAndHideToast(
        toastId,
        {
          title: successMessage,
          variant: 'success',
        },
        duplicate
          ? undefined
          : {
              page: 'folder',
              projectId,
              folderAssetId: destination.id,
            }
      );

      onSuccess?.();
    };

    await copyAssets({
      variables: {
        input: {
          assetIds,
          copyComments,
          parentId: destination.id,
          renameAssetCopy: duplicate,
        },
      },
      errorPolicy: 'all',
    })
      .then(handleResolve, handleError)
      .finally(clearFocusedAsset);
  };
};
